import axios from "axios";
import { api } from "../../mockData";
import { toast } from "react-toastify";
import {
  SIP_TRACE_START_REQUEST,
  SIP_TRACE_START_SUCCESS,
  SIP_TRACE_START_FAIL,
  SIP_TRACE_STOP_REQUEST,
  SIP_TRACE_STOP_SUCCESS,
  SIP_TRACE_STOP_FAIL,
  SIP_TRACE_STATUS_REQUEST,
  SIP_TRACE_STATUS_SUCCESS,
  SIP_TRACE_STATUS_FAIL,
  SIP_TRACE_MESSAGES_REQUEST,
  SIP_TRACE_MESSAGES_SUCCESS,
  SIP_TRACE_MESSAGES_FAIL,
  SIP_TRACE_CALL_MESSAGES_REQUEST,
  SIP_TRACE_CALL_MESSAGES_SUCCESS,
  SIP_TRACE_CALL_MESSAGES_FAIL,
  SIP_TRACE_ERRORS_REQUEST,
  SIP_TRACE_ERRORS_SUCCESS,
  SIP_TRACE_ERRORS_FAIL,
  SIP_TRACE_MESSAGE_DETAIL_REQUEST,
  SIP_TRACE_MESSAGE_DETAIL_SUCCESS,
  SIP_TRACE_MESSAGE_DETAIL_FAIL,
  SIP_TRACE_NEW_MESSAGE,
  SIP_TRACE_BATCH_MESSAGES,
  SIP_TRACE_STATS_UPDATE,
  SIP_TRACE_CAPTURE_STOPPED,
  SIP_TRACE_CLEAR,
} from "../constants/sipTraceConstants";

const getAuthHeaders = () => {
  const token = JSON.parse(localStorage.getItem("admin"));
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token?.access_token}`,
  };
};

// --- Capture Control ---

export const startSipTrace = (filters = {}) => async (dispatch) => {
  try {
    dispatch({ type: SIP_TRACE_START_REQUEST });
    const { data } = await axios.post(
      `${api.dev}/api/siptrace/start`,
      filters,
      { headers: getAuthHeaders() }
    );
    if (data?.status === 200) {
      toast.success(data?.message || "SIP Tracer started");
      dispatch({ type: SIP_TRACE_START_SUCCESS, payload: data });
    } else {
      toast.warning(data?.message || "Could not start capture");
      dispatch({ type: SIP_TRACE_START_FAIL, payload: data?.message });
    }
  } catch (error) {
    const msg = error?.response?.data?.message || "Failed to start SIP Tracer";
    toast.error(msg);
    dispatch({ type: SIP_TRACE_START_FAIL, payload: msg });
  }
};

export const stopSipTrace = () => async (dispatch) => {
  try {
    dispatch({ type: SIP_TRACE_STOP_REQUEST });
    const { data } = await axios.post(
      `${api.dev}/api/siptrace/stop`,
      {},
      { headers: getAuthHeaders() }
    );
    if (data?.status === 200) {
      toast.success(data?.message || "SIP Tracer stopped");
      dispatch({ type: SIP_TRACE_STOP_SUCCESS, payload: data });
    } else {
      toast.warning(data?.message || "Could not stop capture");
      dispatch({ type: SIP_TRACE_STOP_FAIL, payload: data?.message });
    }
  } catch (error) {
    const msg = error?.response?.data?.message || "Failed to stop SIP Tracer";
    toast.error(msg);
    dispatch({ type: SIP_TRACE_STOP_FAIL, payload: msg });
  }
};

// --- Data Fetching ---

export const getSipTraceStatus = () => async (dispatch) => {
  try {
    dispatch({ type: SIP_TRACE_STATUS_REQUEST });
    const { data } = await axios.get(`${api.dev}/api/siptrace/status`, {
      headers: getAuthHeaders(),
    });
    dispatch({ type: SIP_TRACE_STATUS_SUCCESS, payload: data?.data });
  } catch (error) {
    dispatch({
      type: SIP_TRACE_STATUS_FAIL,
      payload: error?.response?.data?.message || "Failed to get status",
    });
  }
};

export const getSipTraceMessages =
  (filters = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: SIP_TRACE_MESSAGES_REQUEST });
      const params = new URLSearchParams();
      if (filters.method) params.append("method", filters.method);
      if (filters.phone) params.append("phone", filters.phone);
      if (filters.call_id) params.append("call_id", filters.call_id);
      if (filters.direction) params.append("direction", filters.direction);
      if (filters.ip) params.append("ip", filters.ip);
      if (filters.errors_only) params.append("errors_only", "true");

      const queryString = params.toString();
      const url = `${api.dev}/api/siptrace/messages${queryString ? "?" + queryString : ""}`;

      const { data } = await axios.get(url, { headers: getAuthHeaders() });
      dispatch({
        type: SIP_TRACE_MESSAGES_SUCCESS,
        payload: data?.data || [],
      });
    } catch (error) {
      dispatch({
        type: SIP_TRACE_MESSAGES_FAIL,
        payload: error?.response?.data?.message || "Failed to get messages",
      });
    }
  };

export const getSipTraceCallMessages = (callId) => async (dispatch) => {
  try {
    dispatch({ type: SIP_TRACE_CALL_MESSAGES_REQUEST });
    const { data } = await axios.get(
      `${api.dev}/api/siptrace/call/${encodeURIComponent(callId)}`,
      { headers: getAuthHeaders() }
    );
    dispatch({
      type: SIP_TRACE_CALL_MESSAGES_SUCCESS,
      payload: data?.data || [],
    });
  } catch (error) {
    dispatch({
      type: SIP_TRACE_CALL_MESSAGES_FAIL,
      payload: error?.response?.data?.message || "Failed to get call messages",
    });
  }
};

export const getSipTraceErrors = () => async (dispatch) => {
  try {
    dispatch({ type: SIP_TRACE_ERRORS_REQUEST });
    const { data } = await axios.get(`${api.dev}/api/siptrace/errors`, {
      headers: getAuthHeaders(),
    });
    dispatch({ type: SIP_TRACE_ERRORS_SUCCESS, payload: data?.data || [] });
  } catch (error) {
    dispatch({
      type: SIP_TRACE_ERRORS_FAIL,
      payload: error?.response?.data?.message || "Failed to get errors",
    });
  }
};

export const getSipTraceMessageDetail = (msgId) => async (dispatch) => {
  try {
    dispatch({ type: SIP_TRACE_MESSAGE_DETAIL_REQUEST });
    const { data } = await axios.get(
      `${api.dev}/api/siptrace/message/${msgId}`,
      { headers: getAuthHeaders() }
    );
    dispatch({
      type: SIP_TRACE_MESSAGE_DETAIL_SUCCESS,
      payload: data?.data || null,
    });
  } catch (error) {
    dispatch({
      type: SIP_TRACE_MESSAGE_DETAIL_FAIL,
      payload: error?.response?.data?.message || "Message not found",
    });
  }
};

// --- Real-time SocketIO dispatchers ---

export const sipTraceNewMessage = (message) => ({
  type: SIP_TRACE_NEW_MESSAGE,
  payload: message,
});

export const sipTraceBatchMessages = (messages) => ({
  type: SIP_TRACE_BATCH_MESSAGES,
  payload: messages,
});

export const sipTraceStatsUpdate = (stats) => ({
  type: SIP_TRACE_STATS_UPDATE,
  payload: stats,
});

export const sipTraceCaptureStopped = (reason) => ({
  type: SIP_TRACE_CAPTURE_STOPPED,
  payload: reason,
});

export const clearSipTrace = () => ({
  type: SIP_TRACE_CLEAR,
});
